import { useEffect, useState, useCallback } from 'react'

interface CompletionModalProps {
  isOpen: boolean
  totalShards: number
  projectName: string
  getAuthHeaders: () => Record<string, string>
  onDismiss: () => void
}

export function CompletionModal({ isOpen, totalShards, projectName, getAuthHeaders, onDismiss }: CompletionModalProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [completedAt] = useState(() => new Date())
  const [isDownloading, setIsDownloading] = useState(false)

  const handleDownload = useCallback(async () => {
    setIsDownloading(true)
    try {
      const response = await fetch(`/api/projects/${encodeURIComponent(projectName)}/download`, {
        headers: getAuthHeaders(),
      })
      if (!response.ok) throw new Error('Download failed')
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${projectName}.zip`
      a.click()
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to download project:', error)
    } finally {
      setIsDownloading(false)
    }
  }, [projectName, getAuthHeaders])

  useEffect(() => {
    if (isOpen) {
      // Trigger enter animation
      const timer = setTimeout(() => setIsVisible(true), 10)
      return () => clearTimeout(timer)
    } else {
      setIsVisible(false)
    }
  }, [isOpen])

  if (!isOpen) {
    return null
  }

  const formattedTime = completedAt.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })

  const formattedDate = completedAt.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

  return (
    <div
      className={`
        fixed inset-0 z-50 flex items-center justify-center
        transition-all duration-300 ease-out
        ${isVisible ? 'bg-black/60 backdrop-blur-sm' : 'bg-transparent'}
      `}
      onClick={onDismiss}
    >
      <div
        className={`
          themed-panel border border-cosmic-nebula/50 rounded-xl shadow-2xl
          backdrop-blur-md bg-cosmic-abyss/95
          transform transition-all duration-300 ease-out
          max-w-md w-full mx-4 overflow-hidden
          ${isVisible ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}
        `}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with glow effect */}
        <div className="relative px-6 pt-8 pb-6 text-center">
          {/* Subtle glow behind the icon */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="w-32 h-32 bg-cosmic-nebula/20 rounded-full blur-3xl" />
          </div>

          {/* Success icon */}
          <div className="relative w-16 h-16 mx-auto mb-4 rounded-full bg-cosmic-nebula/20 border border-cosmic-nebula/40 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-cosmic-nebula"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M5 13l4 4L19 7" />
            </svg>
          </div>

          {/* Title */}
          <h2 className="relative text-2xl font-bold text-white mb-2">
            All Shards Complete!
          </h2>

          {/* Subtitle */}
          <p className="relative text-gray-400 text-sm">
            Mission accomplished. All work has been completed successfully.
          </p>
        </div>

        {/* Stats section */}
        <div className="px-6 pb-6">
          <div className="bg-cosmic-surface/50 rounded-lg p-4 border border-cosmic-border">
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <p className="text-3xl font-bold text-cosmic-nebula">{totalShards}</p>
                <p className="text-xs text-gray-500 uppercase tracking-wide mt-1">
                  Total Shards
                </p>
              </div>
              <div>
                <p className="text-lg font-medium text-white">{formattedTime}</p>
                <p className="text-xs text-gray-500 uppercase tracking-wide mt-1">
                  {formattedDate}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer with download and dismiss buttons */}
        <div className="px-6 pb-6 space-y-2">
          <button
            onClick={handleDownload}
            disabled={isDownloading}
            className="
              w-full py-3 px-4 rounded-lg font-medium text-sm
              bg-cosmic-nebula hover:bg-cosmic-nebula/80 disabled:opacity-50
              text-white transition-colors duration-200
              focus:outline-none focus:ring-2 focus:ring-cosmic-nebula/50 focus:ring-offset-2 focus:ring-offset-cosmic-abyss
              flex items-center justify-center gap-2
            "
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            {isDownloading ? 'Downloading...' : 'Download Repository'}
          </button>
          <button
            onClick={onDismiss}
            className="
              w-full py-3 px-4 rounded-lg font-medium text-sm
              text-gray-400 hover:text-white hover:bg-white/5
              transition-colors duration-200
              focus:outline-none focus:ring-2 focus:ring-cosmic-nebula/50 focus:ring-offset-2 focus:ring-offset-cosmic-abyss
            "
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
